import { useMemo, useState } from 'react'
import useBalances from './useBalances'
import { useCurrentWallet, useSwapSlippage, useUserTokens } from '../state'
import { useSwapContext } from '../providers/SwapProvider'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { logger } from '../logger'
import { SWAP_COMMISSION } from '../constants/business'
import { SwapTokenPair } from '../interfaces/swapInterface'
import { GAEventName } from '../interfaces/googleAnalytics'
import LedgerScannerClient from '../clients/LedgerScannerClient'
import { Chain } from '../interfaces/common'

interface Props {
  performSwap: (pair: SwapTokenPair, quote: number) => Promise<number>
  onError: (error: string) => void
  close: () => void
  chain?: string
}

const SUCCESS_TIMEOUT = 3000

const useSwapReview = ({ performSwap, onError, close, chain }: Props) => {
  const { refreshBalance } = useBalances()
  const { currentWallet } = useCurrentWallet()
  const { addToken } = useUserTokens()
  const { fromToken, toToken, amount, toAmount, selectedProvider, quotes, setSwapReviewVisible } =
    useSwapContext()

  const [userApprovedToAmount, setUserApprovedToAmount] = useState('')
  const [swapLoading, setSwapLoading] = useState(false)
  const [swapSuccess, setSwapSuccess] = useState(false)
  const { slippage } = useSwapSlippage()
  const { fireEvent } = useGoogleAnalytics()

  const quote = useMemo(() => {
    return quotes.find((q) => q.pair.provider === selectedProvider)
  }, [quotes, selectedProvider])

  const swapDetails = useMemo(() => {
    return [
      { title: 'Swapping via', value: selectedProvider },
      {
        title: 'Fee',
        value: `${SWAP_COMMISSION * 100}% + transaction fees`,
      },
      {
        title: 'Max slippage',
        value: `${slippage}%`,
        adjustable: true,
      },
    ]
  }, [selectedProvider, slippage])

  const doSwap = async () => {
    if (!quote) return

    setSwapLoading(true)
    setUserApprovedToAmount(toAmount)

    try {
      const received = await performSwap(quote.pair, quote.price)

      fireEvent(GAEventName.SWAP, {
        type: 'swap',
        from_token: fromToken!.symbol,
        to_token: toToken!.symbol,
        amount: Number(amount),
        amount_received: received / 10 ** toToken!.decimals,
      })

      setSwapSuccess(true)

      addToken(currentWallet, toToken!.id)

      LedgerScannerClient.refreshSwaps(currentWallet)

      setTimeout(() => {
        refreshBalance(fromToken!)
        refreshBalance(toToken!)
        setSwapReviewVisible(false)
        close()
      }, SUCCESS_TIMEOUT)
    } catch (e) {
      logger.error('Swap failed', { originalError: e, fromToken, toToken })
      onError((e as Error).toString())
    }

    setSwapLoading(false)
    setUserApprovedToAmount('')
  }

  return {
    userApprovedToAmount,
    swapLoading,
    swapSuccess,
    swapDetails,
    doSwap,
  }
}

export default useSwapReview
